import { TopNavigationBar, TopNavigationLink, TopNavigationMenuButton } from '@/layouts/components/TopNavigationBar';
import { InertiaLinkProps, Link, usePage } from '@inertiajs/react';
import Icon from '@mdi/react';
import {
    mdiAccount,
    mdiArrowTopRight,
    mdiCheckBold,
    mdiCog,
    mdiEmailHeartOutline,
    mdiHome,
    mdiLogout,
    mdiMapMarker,
    mdiMenu,
    mdiNotebook,
    mdiTimelineCheck,
} from '@mdi/js';
import { UserAvatar } from '@/components/UserAvatar';
import Dropdown from '@/components/Dropdown';
import { usePostHog } from 'posthog-js/react';
import { getFeedbackFormURL } from '@/utils';
import { AppHead } from '@/pages/components/AppHead';
import { useEffect, useState } from 'react';
import { Divider } from '@/trailmix/Divider';
import { SiteFooter } from './components/SiteFooter';
import { useAuth } from '@/auth/useAuth';
import { User } from '@/types';
import { SearchBarButton } from './components/SearchBarButton';
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner';

function MobileNavMenuItem({
    iconPath,
    external = false,
    as,
    method,
    href,
    target,
    rel,
    children,
}: {
    iconPath: string;
    external?: boolean;
    href?: string;
} & InertiaLinkProps) {
    const Component = external ? 'a' : Link;

    return (
        <Component
            as={as}
            method={method}
            href={href}
            target={target}
            rel={rel}
            className='flex items-center gap-2 rounded-md px-3 py-2 font-medium text-gray-700 focus:outline-none focus:ring active:bg-gray-100'
        >
            <Icon path={iconPath} className='size-5' />
            {children}
        </Component>
    );
}

export type AuthenticatedLayoutProps = {
    children: React.ReactNode;
};

export default function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
    const auth = useAuth() as { user: User };
    const posthog = usePostHog();
    const [isMobileNavMenuOpen, setIsMobileNavMenuOpen] = useState(false);
    const { url } = usePage();

    useEffect(() => {
        posthog?.identify(auth.user.id.toString(), {
            email: auth.user.email,
            name: auth.user.name,
            username: auth.user.username,
        });
    }, [auth.user.id]);

    // Mobile nav menu stays open when navigating between persistent layouts
    // (e.g. Visited -> Planning) so we need to close it ourselves.
    useEffect(() => {
        setIsMobileNavMenuOpen(false);
    }, [url]);

    return (
        <div className='flex min-h-screen flex-col'>
            <AppHead />
            <TopNavigationBar
                left={
                    <>
                        {/* Desktop nav items */}
                        <TopNavigationLink href={route('planning-entries.index', [auth.user])}>
                            Planning
                        </TopNavigationLink>
                        <TopNavigationLink href={route('visits.index', [auth.user])}>Visited</TopNavigationLink>
                        <TopNavigationLink href={route('logbook.index', [auth.user])}>Logbook</TopNavigationLink>
                        <TopNavigationLink href={route('lists.index', [auth.user])}>Lists</TopNavigationLink>
                    </>
                }
                center={<SearchBarButton className='max-w-lg desktop:hidden' />}
                right={
                    <>
                        <SearchBarButton className='hidden w-40 max-w-lg desktop:flex lg:w-80' />

                        {/* Mobile menu button */}
                        <TopNavigationMenuButton
                            onClick={() => setIsMobileNavMenuOpen(b => !b)}
                            className='desktop:hidden'
                        >
                            <span className='inline-flex text-gray-700'>
                                <Icon path={mdiMenu} className='size-6' />
                            </span>
                        </TopNavigationMenuButton>

                        {/* Desktop menu button */}
                        <div className='hidden desktop:block'>
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <TopNavigationMenuButton>
                                        <span className='hidden desktop:inline-flex'>
                                            <UserAvatar size='sm' user={auth.user} />
                                        </span>
                                    </TopNavigationMenuButton>
                                </Dropdown.Trigger>

                                <Dropdown.Content>
                                    <Dropdown.Link
                                        href={route('profile.show', [auth.user])}
                                        className='inline-flex items-center gap-2'
                                    >
                                        <Icon path={mdiAccount} className='size-4' />
                                        Profile
                                    </Dropdown.Link>
                                    <Dropdown.Link
                                        href={route('profile.edit')}
                                        className='inline-flex items-center gap-2'
                                    >
                                        <Icon path={mdiCog} className='size-4' />
                                        Settings
                                    </Dropdown.Link>
                                    <a
                                        href={getFeedbackFormURL(auth.user.email)}
                                        className='inline-flex w-full items-center gap-2 px-4 py-2 text-left text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 focus:bg-gray-100 focus:outline-none'
                                        target='_blank'
                                        rel='noreferrer noopener'
                                    >
                                        <Icon path={mdiArrowTopRight} className='size-4' />
                                        Give feedback
                                    </a>
                                    <Dropdown.Link
                                        href={route('logout')}
                                        method='post'
                                        as='button'
                                        onSuccess={() => posthog?.reset()}
                                        className='inline-flex items-center gap-2'
                                    >
                                        <Icon path={mdiLogout} className='size-4' />
                                        Log Out
                                    </Dropdown.Link>
                                </Dropdown.Content>
                            </Dropdown>
                        </div>
                    </>
                }
                mobileNavMenu={
                    isMobileNavMenuOpen && (
                        <div className='desktop:hidden'>
                            {/* Mobile nav menu popout */}
                            <div className='space-y-1 border-t-2 px-2 pb-3 pt-2'>
                                <MobileNavMenuItem href={route('home')} iconPath={mdiHome}>
                                    Home
                                </MobileNavMenuItem>
                                <MobileNavMenuItem href={route('profile.show', [auth.user])} iconPath={mdiAccount}>
                                    Profile
                                </MobileNavMenuItem>
                                <MobileNavMenuItem
                                    href={route('planning-entries.index', [auth.user])}
                                    iconPath={mdiMapMarker}
                                >
                                    Planning
                                </MobileNavMenuItem>
                                <MobileNavMenuItem href={route('visits.index', [auth.user])} iconPath={mdiCheckBold}>
                                    Visited
                                </MobileNavMenuItem>
                                <MobileNavMenuItem
                                    href={route('logbook.index', [auth.user])}
                                    iconPath={mdiTimelineCheck}
                                >
                                    Logbook
                                </MobileNavMenuItem>
                                <MobileNavMenuItem href={route('lists.index', [auth.user])} iconPath={mdiNotebook}>
                                    Lists
                                </MobileNavMenuItem>

                                <Divider />

                                <MobileNavMenuItem href={route('profile.edit')} iconPath={mdiCog}>
                                    Settings
                                </MobileNavMenuItem>
                                <MobileNavMenuItem
                                    external
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    href={getFeedbackFormURL(auth.user.email)}
                                    iconPath={mdiEmailHeartOutline}
                                >
                                    Give Feedback
                                </MobileNavMenuItem>
                                <MobileNavMenuItem
                                    as='button'
                                    method='post'
                                    href={route('logout')}
                                    iconPath={mdiLogout}
                                >
                                    Log Out
                                </MobileNavMenuItem>
                            </div>
                        </div>
                    )
                }
            />
            <EmailVerificationBanner />
            {children}
            <SiteFooter />
        </div>
    );
}
